import { useCallback, useMemo } from 'react'
import type { CSSProperties, MouseEvent, ReactNode } from 'react'
import { tokenizeLinks, fileIdFromEzActions } from '@tfa/shared'
import { useTelegram } from '../hooks/useTelegram'

const LINK_STYLE: CSSProperties = {
  color: '#2F6FED',
  textDecoration: 'underline',
  wordBreak: 'break-word',
}

const isTelegramHref = (href: string): boolean =>
  href.startsWith('tg://') ||
  /^(?:https?:\/\/)?(?:www\.)?(?:t\.me|telegram\.me)\b/i.test(href)

/**
 * Открывает href через подходящий Telegram SDK-метод. Используется и для
 * линкифицированного plain-текста, и для делегированных кликов по <a> в HTML.
 */
export function useOpenHref() {
  const { openLink, openTelegramLink } = useTelegram()
  return useCallback(
    (href: string) => {
      if (!href || href.startsWith('#')) return
      ;(isTelegramHref(href) ? openTelegramLink : openLink)(href)
    },
    [openLink, openTelegramLink],
  )
}

/**
 * Инлайн-вариант: возвращает фрагмент с текстом и кликабельными ссылками,
 * БЕЗ собственной обёртки. Кладётся внутрь уже стилизованного элемента
 * (например `<p ref>` с line-clamp). Клик по ссылке не всплывает — безопасно
 * внутри кликабельных карточек.
 */
export function LinkText({ children }: { children: string | null | undefined }): JSX.Element {
  const open = useOpenHref()
  const tokens = useMemo(() => tokenizeLinks(children), [children])

  const onClick = useCallback(
    (e: MouseEvent<HTMLAnchorElement>, href: string) => {
      e.preventDefault()
      e.stopPropagation()
      open(href)
    },
    [open],
  )

  const nodes: ReactNode[] = tokens.map((tok, i) =>
    tok.type === 'link' ? (
      <a key={i} href={tok.href} onClick={(e) => onClick(e, tok.href)} style={LINK_STYLE}>
        {tok.value}
      </a>
    ) : (
      tok.value
    ),
  )

  return <>{nodes}</>
}

/**
 * Рендерит свободный текст в собственной обёртке (`as`), превращая URL / t.me /
 * @username в кликабельные ссылки. Ссылки открываются через Telegram SDK,
 * а не уводят из мини-аппа дефолтной навигацией.
 */
export function Linkify({
  children,
  style,
  className,
  as: As = 'span',
}: {
  children: string | null | undefined
  style?: CSSProperties
  className?: string
  as?: 'span' | 'p' | 'div'
}): JSX.Element {
  return (
    <As className={className} style={style}>
      <LinkText>{children}</LinkText>
    </As>
  )
}

/**
 * onClick-делегат для контейнеров с HTML через dangerouslySetInnerHTML:
 * перехватывает клики по <a href> и открывает их через Telegram SDK,
 * чтобы ссылки внутри rich-text (теория, описание челленджа) работали в WebView.
 *
 * `onFile` — обработчик вложений-файлов XL (см. fileIdFromEzActions). Без него
 * такой тап по-прежнему ничего не делает: адреса у ссылки просто нет.
 */
export function useHtmlLinkHandler(opts?: { onFile?: (fileId: string) => void }) {
  const open = useOpenHref()
  const onFile = opts?.onFile
  return useCallback(
    (e: MouseEvent<HTMLElement>) => {
      const target = e.target as HTMLElement | null
      // #300: XL-разметка вложений («Скачать гайд») бывает <a> без href — URL
      // лежит в data-атрибутах. Ловим и такие, иначе тап по вложению в лекции
      // молча глохнет в Telegram WebView.
      const anchor = target?.closest?.('a, [data-href], [data-url], [data-file], [ezactions]') as HTMLElement | null
      if (!anchor) return

      // #413: файл-вложение опознаём ДО href — у таких <a> href отсутствует
      // вовсе, и без этой ветки тап оставался немым.
      const fileId = fileIdFromEzActions(anchor.getAttribute('ezactions'))
      if (fileId && onFile) {
        e.preventDefault()
        e.stopPropagation()
        onFile(fileId)
        return
      }

      const href = anchor.getAttribute('href')
        || anchor.getAttribute('data-href')
        || anchor.getAttribute('data-url')
        || anchor.getAttribute('data-file')
        || ''
      if (!href || href.startsWith('#')) return
      // Относительный путь (XL-хост нам неизвестен) — не перехватываем,
      // оставляем браузеру дефолтное поведение.
      if (href.startsWith('/')) return
      e.preventDefault()
      e.stopPropagation()
      open(href)
    },
    [open, onFile],
  )
}
